"use client";

import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Github,
  Copy,
  Sparkles,
} from "lucide-react";
import { Button } from "../ui/Button";

type Template = {
  id: string;
  name: string;
  category: string;
  image: string;
  tags: string[];
  liveUrl: string;
  repoUrl: string;
};

const templates: Template[] = [
  {
    id: "775526348",
    name: "Adex",
    category: "Business/Corporate",
    image:
      "https://nhrbeqkofflgyctvxhac.supabase.co/storage/v1/object/public/template-previews/thumbnails/adex_index.html-25.png",
    tags: ["index.html"],
    liveUrl: "https://template-previews.vercel.app/adex/index.html",
    repoUrl:
      "https://github.com/shadcn-ui/templates/tree/main/templates/public/adex",
  },
  {
    id: "775551416",
    name: "Agen Bootstrap",
    category: "Computers and Technology",
    image:
      "https://nhrbeqkofflgyctvxhac.supabase.co/storage/v1/object/public/template-previews/thumbnails/agen-bootstrap_index.html-25.png",
    tags: ["about.html"],
    liveUrl: "https://template-previews.vercel.app/agen-bootstrap/index.html",
    repoUrl:
      "https://github.com/shadcn-ui/templates/tree/main/templates/public/agen-bootstrap",
  },
  {
    id: "775557859",
    name: "Grilli",
    category: "Food",
    image:
      "https://nhrbeqkofflgyctvxhac.supabase.co/storage/v1/object/public/template-previews/thumbnails/grilli_index.html-25.png",
    tags: ["index.html"],
    liveUrl: "https://template-previews.vercel.app/grilli/index.html",
    repoUrl:
      "https://github.com/shadcn-ui/templates/tree/main/templates/public/grilli",
  },
  {
    id: "775564578",
    name: "Business Porto 1",
    category: "Business/Corporate",
    image:
      "https://nhrbeqkofflgyctvxhac.supabase.co/storage/v1/object/public/template-previews/thumbnails/business-porto-1_index.html-25.png",
    tags: ["index.html"],
    liveUrl: "https://template-previews.vercel.app/business-porto-1/index.html",
    repoUrl:
      "https://github.com/shadcn-ui/templates/tree/main/templates/public/business-porto-1",
  },
];

const categories = [
  "All",
  "Business/Corporate",
  "Computers and Technology",
  "Food"
];

export function TemplateScrollerShowcase() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [hoveredTemplate, setHoveredTemplate] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const filteredTemplates =
    activeCategory === "All"
      ? templates
      : templates.filter((template) => template.category === activeCategory);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  return (
    <section className="py-24 px-4 relative overflow-hidden " id="templates">
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-card/80 to-transparent "></div>
      <div className="container mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Templates & Ecosystem
          </h2>
          <p className="text-xl text-card-foreground/30 max-w-3xl mx-auto">
            Start with a template or import from GitHub to accelerate your
            development
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === category
                  ? "bg-purple-600 text-white"
                  : "bg-gray-800 text-card-foreground/30 hover:bg-gray-700"
                }`}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="relative max-w-6xl mx-auto">
          {/* <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute -top-16 right-4 md:right-8 px-5 py-3 rounded-lg bg-gray-800/90 backdrop-blur-sm border border-gray-700 font-medium shadow-lg flex items-center space-x-2 cursor-pointer hover:bg-gray-700/90 transition-colors"
          >
            <Github className="h-4 w-4" />
            <span>Import from GitHub</span>
          </motion.div> */}

          <div className="relative">
            <button
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 text-white hover:bg-black/80 transition-colors"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>

            <div
              ref={scrollContainerRef}
              className="flex overflow-x-auto pb-8 hide-scrollbar snap-x snap-mandatory"
              style={{ scrollbarWidth: "none" }}
            >
              {filteredTemplates.map((template, index) => (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1, duration: 0.3 }}
                  className="min-w-[300px] md:min-w-[350px] snap-center px-4"
                  onMouseEnter={() => setHoveredTemplate(template.id)}
                  onMouseLeave={() => setHoveredTemplate(null)}
                >
                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-pink-600/20 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl"></div>

                    <div className="relative bg-gray-900 border border-gray-800 rounded-xl overflow-hidden transition-transform duration-300 group-hover:scale-[1.02] group-hover:shadow-xl group-hover:shadow-purple-500/10">
                      <div className="h-48 overflow-hidden">
                        <img
                          src={template.image || "/placeholder.svg"}
                          alt={template.name}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />

                        {hoveredTemplate === template.id && (
                          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                            <div className="flex space-x-3">
                              <motion.button
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                <Button
                                  href={`${template.liveUrl}`}
                                  className="px-4 py-2 rounded-lg bg-purple-600 text-white font-medium flex items-center space-x-2 hover:bg-purple-700 transition-colors"
                                >
                                  <Sparkles className="h-4 w-4" />
                                  <span>Preview</span>
                                </Button>
                              </motion.button>
                              <motion.button
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2, delay: 0.1 }}
                              >
                                <Button
                                  href={`${template.repoUrl}`}
                                  className="px-4 py-2 rounded-lg bg-gray-800 text-white font-medium flex items-center space-x-2 hover:bg-gray-700 transition-colors"
                                >
                                  <Copy className="h-4 w-4" />
                                  <span>Use</span>
                                </Button>
                              </motion.button>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="p-4">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-bold text-lg">{template.name}</h3>
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-800">
                            {template.category}
                          </span>
                        </div>

                        <div className="flex flex-wrap gap-2 mt-3">
                          {template.tags.map((tag) => (
                            <span
                              key={tag}
                              className="text-xs px-2 py-1 rounded-full bg-purple-900/30 text-purple-300 border border-purple-500/30"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="absolute top-3 right-3">
                        <div className="h-8 w-8 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 flex items-center justify-center">
                          <Sparkles className="h-4 w-4 text-purple-300" />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <button
              onClick={scrollRight}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 text-white hover:bg-black/80 transition-colors"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>

          <div className="flex justify-center mt-8">
            <div className="flex space-x-2">
              {Array.from({
                length: Math.ceil(filteredTemplates.length / 3),
              }).map((_, index) => (
                <button
                  key={index}
                  className={`h-2 w-2 rounded-full transition-colors ${index === 0 ? "bg-purple-500" : "bg-gray-700"}`}
                ></button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
